import React from 'react';
import { Avatar, Box, Button, Flex, Heading, Text } from '@/components/ui';
import {
  IconCheck,
  IconCloudCheck,
  IconCloudUpload,
  IconExclamationCircle,
  IconFiles,
  IconLoader2,
  IconReload,
} from '@tabler/icons-react';
import { StepStatus, ValidationStep } from '../types';

interface ValidationProgressBarProps {
  currentStep: ValidationStep;
  hasErrors?: boolean;
  stepStatuses: Record<ValidationStep, StepStatus>;
  onStartNew?: () => void;
}

const STEPS: Array<{
  id: ValidationStep;
  label: string;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  description: string;
}> = [
  {
    id: 'file-selection',
    label: 'File Selection',
    icon: IconFiles,
    description: 'Select files and resolve duplicates',
  },
  {
    id: 'server-validation',
    label: 'Server Validation',
    icon: IconCloudCheck,
    description: 'Validate files against server rules',
  },
  {
    id: 'ready-to-upload',
    label: 'Ready to Upload',
    icon: IconCloudUpload,
    description: 'Files are ready for upload',
  },
  {
    id: 'upload-complete',
    label: 'Upload Complete',
    icon: IconCheck,
    description: 'Upload finished - view results',
  },
];

export const ValidationProgressBar: React.FC<ValidationProgressBarProps> = ({
  currentStep,
  hasErrors = false,
  stepStatuses,
  onStartNew,
}) => {
  void hasErrors;
  const currentStepIndex = STEPS.findIndex(step => step.id === currentStep);

  const getStepStatus = (stepId: ValidationStep, index: number): StepStatus => {
    return (
      stepStatuses[stepId] ||
      (index <= currentStepIndex ? 'current' : 'pending')
    );
  };

  const getStepIcon = (
    step: {
      icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
    },
    status: StepStatus
  ) => {
    const IconComponent = step.icon;
    const iconSize = 16;

    if (status === 'processing') {
      return (
        <IconLoader2
          size={iconSize}
          style={{
            color: 'var(--blue-9)',
            animation: 'spin 1s linear infinite',
          }}
        />
      );
    }

    if (status === 'completed') {
      return <IconCheck size={iconSize} style={{ color: 'var(--green-9)' }} />;
    }

    if (status === 'error') {
      return (
        <IconExclamationCircle
          size={iconSize}
          style={{ color: 'var(--red-9)' }}
        />
      );
    }

    return (
      <IconComponent
        size={iconSize}
        style={{
          color:
            status === 'current'
              ? 'var(--blue-9)'
              : 'var(--gray-9)',
        }}
      />
    );
  };

  return (
    // <Card
    //   mb="4"
    //   size="xsmall"
    //   className="upload-progress-card"
    //   style={{
    //     backgroundColor: 'var(--gray-2)',
    //     position: 'static',
    //     // position: 'sticky',
    //     // bottom: 0,
    //   }}
    // >
    <Flex direction="row" gap="3" align="center" justify="between">
      <Heading weight="medium" size="2xsmall" mr="4">
        Upload Progress
      </Heading>

      <Flex align="center" gap="4" style={{ overflowX: 'auto' }}>
        {STEPS.map((step, index) => {
          const status = getStepStatus(step.id, index);
          const isLast = index === STEPS.length - 1;

          return (
            <React.Fragment key={step.id}>
              <Flex align="center" gap="2" style={{ minWidth: 'fit-content' }}>
                <Avatar
                  size="small"
                  variant="soft"
                  color={
                    status === 'completed'
                      ? 'success'
                      : status === 'error'
                        ? 'error'
                        : status === 'processing'
                          ? 'info'
                          : status === 'current'
                            ? 'primary'
                            : 'gray'
                  }
                >
                  {getStepIcon(step, status)}
                </Avatar>
                <Text
                  size="2xsmall"
                  align="center"
                  weight={
                    status === 'current' || status === 'processing'
                      ? 'medium'
                      : 'regular'
                  }
                  color={
                    status === 'completed'
                      ? 'success'
                      : status === 'error'
                        ? 'error'
                        : status === 'current' || status === 'processing'
                          ? 'primary'
                          : 'gray'
                  }
                >
                  {step.label}
                </Text>
              </Flex>

              {!isLast && (
                <Box
                  style={{
                    width: 16,
                    height: 1,
                    backgroundColor:
                      index < currentStepIndex ||
                      (status === 'completed' &&
                        getStepStatus(STEPS[index + 1].id, index + 1) !==
                          'pending')
                        ? 'var(--green-7)'
                        : 'var(--gray-7)',
                    margin: '0 4px',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Flex>

      {/* Start New button */}
      <Button
        variant="outlined"
        size="small"
        onClick={onStartNew}
        startIcon={<IconReload />}
      >
        Start New
      </Button>
    </Flex>
    // </Card>
  );
};
